import { useEffect } from "react";

/**
 * Imperative <head> updater for SEO pages.
 *
 * Sets <title>, <meta name="description">, canonical link, and OG tags.
 * Cleanly restores the previous title on unmount so navigating away from
 * an SEO page doesn't pollute the rest of the SPA.
 */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export default function SeoHead({ title, description, canonicalPath }) {
  useEffect(() => {
    if (!title) return undefined;
    const previousTitle = document.title;
    document.title = title;

    const origin = window.location.origin;
    const canonicalHref = canonicalPath
      ? origin + (canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`)
      : origin + window.location.pathname;

    const desc = upsertMeta('meta[name="description"]', {
      name: "description",
      content: description || "",
    });
    const canonical = upsertMeta('link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalHref,
    });
    const ogTitle = upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: title,
    });
    const ogDesc = upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description || "",
    });
    const ogUrl = upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalHref,
    });
    const ogType = upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "article",
    });

    return () => {
      document.title = previousTitle;
      [desc, canonical, ogTitle, ogDesc, ogUrl, ogType].forEach((node) => {
        if (node && node.parentNode) node.parentNode.removeChild(node);
      });
    };
  }, [title, description, canonicalPath]);

  return null;
}
